import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, MapPin } from "lucide-react";
import { Kinetic, Reveal, Eyebrow, ParallaxImg } from "@/components/anim";
import { Marquee } from "@/components/Marquee";
import { SectionHead, CTABand } from "@/components/PageHero";
import { IMG } from "@/lib/img";
import { CONTACT } from "@/lib/nav";

const CHAPTERS = [
  {
    num: "01",
    title: "Shop",
    desc: "Turn your online business into a physical storefront.",
    to: "/pop-up-shop",
    img: IMG.store.hooks,
  },
  {
    num: "02",
    title: "Beauty",
    desc: "Rent professional makeup, hair, and nail stations.",
    to: "/beauty-rentals",
    img: IMG.beautyVanity,
  },
  {
    num: "03",
    title: "Create",
    desc: "Host launches, workshops, content days, private events, and brand experiences.",
    to: "/events",
    img: IMG.workshop,
  },
  {
    num: "04",
    title: "Grow",
    desc: "Meet customers, build your brand, test new concepts, and expand without the commitment of your own storefront.",
    to: "/shop-within-a-shop",
    img: IMG.store.counter,
  },
];

const PERFECT_FOR = [
  "Online boutiques",
  "Clothing brands",
  "Jewelry brands",
  "Accessories",
  "Beauty brands",
  "Makeup artists",
  "Hair stylists",
  "Braiders",
  "Wig specialists",
  "Nail technicians",
  "Designers",
  "Creatives",
  "Product launches",
  "Small businesses",
];

const STEPS = [
  "Choose your space or package.",
  "Select your preferred dates.",
  "Bring your products, clients, or brand.",
  "Open your doors and start doing business.",
];

export default function Home() {
  return (
    <>
      {/* ------- Hero ------- */}
      <section className="relative overflow-hidden bg-paper" data-testid="home-hero">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 px-6 pb-20 pt-32 sm:pt-36 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:pb-28 lg:pt-40">
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <Eyebrow>ESSANDTIALS · Bermuda</Eyebrow>
            </Reveal>
            <Kinetic
              delay={0.2}
              lines={[
                <>From Online</>,
                <>
                  To <em>In-Store.</em>
                </>,
              ]}
              className="mt-7 font-serif text-[15vw] font-light uppercase leading-[0.95] text-ink sm:text-7xl lg:text-8xl xl:text-[7.5rem]"
            />
            <Reveal delay={0.7}>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-clay sm:text-xl">
                Your business deserves a space customers can step into.
              </p>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-fog sm:text-base">
                Essandtials is Bermuda&rsquo;s flexible destination for pop-up shops, temporary
                retail, beauty professionals, product launches, events, and growing brands.
              </p>
            </Reveal>
            <Reveal delay={0.85}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/book" data-testid="hero-book-button" className="btn btn-dark">
                  Book Your Space
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
                <Link to="/pricing" data-testid="hero-packages-button" className="btn btn-outline">
                  View Packages
                </Link>
              </div>
            </Reveal>
            <Reveal delay={1}>
              <p className="mt-10 flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-fog" data-testid="hero-location-line">
                <MapPin size={14} strokeWidth={1.5} className="text-sanddeep" />
                39 Dundonald Street, Hamilton — in the heart of Bermuda&rsquo;s busiest retail district
              </p>
            </Reveal>
          </div>

          <div className="relative lg:col-span-5">
            <ParallaxImg
              src={IMG.store.mannequin}
              alt="Inside Essandtials — pink accent wall, gold racks and mannequin at 39 Dundonald Street"
              className="aspect-[3/4] w-full rounded-b-[2rem] rounded-t-[999px] shadow-[0_30px_80px_rgba(26,24,23,0.18)]"
            />
            <Reveal
              delay={0.9}
              className="absolute -left-4 bottom-12 hidden w-44 rotate-[-5deg] rounded-2xl bg-white p-3 shadow-2xl sm:block lg:-left-10"
            >
              <img
                src={IMG.nailPink}
                alt="Manicure detail"
                className="h-36 w-full rounded-xl object-cover"
                loading="lazy"
              />
              <p className="pt-2 pb-1 text-center font-serif text-xs italic text-clay">
                beauty stations, ready for your clients
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "From Online to In-Store",
          "Pop-Up Shops",
          "Retail Rentals",
          "Beauty Chairs",
          "Events & Launches",
          "39 Dundonald Street, Hamilton",
        ]}
      />

      {/* ------- Manifesto chapters ------- */}
      <section className="bg-paper py-24 sm:py-32" data-testid="home-possibilities">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionHead
            eyebrow="What lives here"
            title={
              <>
                One Space.
                <br />
                <em>Endless Possibilities.</em>
              </>
            }
          />
          <div className="mt-16 border-t border-beige/60">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.05}>
                <Link
                  to={c.to}
                  data-testid={`home-chapter-${c.title.toLowerCase()}`}
                  className="group grid grid-cols-12 items-center gap-4 border-b border-beige/60 py-8 transition-colors duration-500 hover:bg-sand/60 sm:py-10"
                >
                  <span className="col-span-2 pl-2 font-serif text-sm italic text-sanddeep sm:col-span-1 sm:pl-4">
                    {c.num}
                  </span>
                  <h3 className="col-span-10 font-serif text-3xl uppercase tracking-[0.04em] text-ink transition-transform duration-500 group-hover:translate-x-2 sm:col-span-4 sm:text-4xl lg:text-5xl">
                    {c.title}
                  </h3>
                  <p className="col-span-10 col-start-3 text-sm leading-relaxed text-clay sm:col-span-5 sm:col-start-6 sm:text-base">
                    {c.desc}
                  </p>
                  <div className="hidden justify-end pr-4 sm:col-span-2 sm:flex">
                    <img
                      src={c.img}
                      alt={c.title}
                      loading="lazy"
                      className="h-20 w-28 rounded-full object-cover grayscale-[0.2] transition-all duration-500 group-hover:grayscale-0"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ------- Your brand. Your shop. Your moment. ------- */}
      <section className="bg-sand py-24 sm:py-32" data-testid="home-your-brand">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
          <div className="order-2 lg:order-1">
            <SectionHead
              eyebrow="Pop-up retail, elevated"
              title={
                <>
                  Your Brand. Your Shop. <em>Your Moment.</em>
                </>
              }
              sub="Essandtials gives entrepreneurs the opportunity to temporarily transform a professional retail space into their own store."
            />
            <Reveal delay={0.3}>
              <p className="mt-8 text-[11px] uppercase tracking-[0.26em] text-fog">
                Perfect for
              </p>
              <div className="mt-4 flex flex-wrap gap-2.5" data-testid="home-perfect-for-chips">
                {PERFECT_FOR.map((p) => (
                  <span key={p} className="chip">
                    {p}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="order-1 lg:order-2">
            <ParallaxImg
              src={IMG.store.racks}
              alt="Inside Essandtials — gold racks, blush ceiling and crystal chandelier"
              className="aspect-[4/5] w-full rounded-3xl shadow-[0_30px_80px_rgba(26,24,23,0.15)]"
            />
          </div>
        </div>
      </section>

      {/* ------- How it works ------- */}
      <section className="bg-ink py-24 text-paper sm:py-32" data-testid="home-how-it-works">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionHead
            light
            eyebrow="How it works"
            title={
              <>
                Four steps.
                <br />
                <em>One unforgettable space.</em>
              </>
            }
          />
          <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-paper/10 bg-paper/10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s} delay={i * 0.1} className="bg-ink p-8 sm:p-10">
                <p className="font-serif text-5xl italic text-blushdeep/80">0{i + 1}</p>
                <p className="mt-6 text-base leading-relaxed text-paper/85">{s}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div className="mt-14 flex justify-center">
              <Link to="/book" data-testid="home-take-offline-button" className="btn btn-blush">
                Take Your Business Offline
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ------- Visit us ------- */}
      <section className="bg-paper py-24 sm:py-32" data-testid="home-visit">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
          <div>
            <ParallaxImg
              src={IMG.store.storefront}
              alt="Outside Essandtials — the storefront at 39 Dundonald Street, Hamilton"
              className="aspect-[4/3] w-full rounded-3xl shadow-[0_30px_80px_rgba(26,24,23,0.12)]"
            />
          </div>
          <div>
            <SectionHead
              eyebrow="Find us"
              title={
                <>
                  In the heart
                  <br />
                  of <em>Hamilton.</em>
                </>
              }
              sub="You'll find Essandtials at 39 Dundonald Street — one of Bermuda's busiest retail districts, steps from Front Street. A professional, beautiful space your customers can simply walk into."
            />
            <Reveal delay={0.3}>
              <p className="mt-8 flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-ink" data-testid="home-address">
                <MapPin size={15} strokeWidth={1.5} className="text-sanddeep" />
                39 Dundonald Street, Hamilton, Bermuda
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ------- Instagram ------- */}
      <section className="bg-cream py-24 sm:py-32" data-testid="home-instagram">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <SectionHead
              eyebrow="Follow the journey"
              title={
                <>
                  @essandtials
                  <br />
                  <em>on Instagram.</em>
                </>
              }
            />
            <Reveal delay={0.2}>
              <a
                href={CONTACT.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-instagram-follow-button"
                className="btn btn-dark"
              >
                Follow @essandtials
              </a>
            </Reveal>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-3 sm:gap-4" data-testid="home-instagram-grid">
            {[
              { src: IMG.store.racks, alt: "Gold racks and chandelier inside Essandtials" },
              { src: IMG.nailPink, alt: "Fresh manicure at the beauty station" },
              { src: IMG.store.mannequin, alt: "Pink accent wall and mannequin styling" },
              { src: IMG.makeupArtist, alt: "Makeup artistry in the studio" },
              { src: IMG.store.counter, alt: "Marble checkout counter and gold tables" },
              { src: IMG.store.storefront, alt: "The Essandtials storefront on Dundonald Street" },
            ].map((t, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <a
                  href={CONTACT.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`home-instagram-tile-${i + 1}`}
                  className="group relative block aspect-square overflow-hidden rounded-2xl"
                >
                  <img
                    src={t.src}
                    alt={t.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-ink/0 opacity-0 transition-all duration-500 group-hover:bg-ink/40 group-hover:opacity-100">
                    <Instagram size={22} strokeWidth={1.5} className="text-paper" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-center text-sm text-clay">
              Pop-ups, new arrivals, beauty days and behind the scenes —{" "}
              <a
                href={CONTACT.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink underline underline-offset-4 hover:text-sanddeep"
              >
                follow along
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand
        title={
          <>
            Your business deserves
            <br />
            <em>a front door.</em>
          </>
        }
        sub="Pop-ups, retail residencies, beauty chairs, events — one space, endless possibilities."
        cta="Book Your Space"
        to="/book"
        testid="home-footer-cta-button"
      />
    </>
  );
}
